import React, { useContext, useEffect, useRef, useState } from "react";
import { formatStringWithPlaceholders } from "../../../../helpers/Utils";
import { ChooseCardStepModel, IChooseCardStepModel } from "../../../../models/buy-giftcard/choose-card/ChooseCardsStep.model";
import { BuyGiftCardStateEnum } from "../../../../models/enums/BuyGiftCardStateEnum";
import { IBuyGiftCardStepProps } from "../../../../models/interfaces/BuyGiftCardStep.interface";
import { AppSettingContext } from "../../../Application";
import LoadingComponent from "../../../shared/LoadingComponent";

interface IChooseCardStepProps extends IBuyGiftCardStepProps {
    stepModel: IChooseCardStepModel;

    handleSubmit(stepModel: IChooseCardStepModel): void;
}

const ChoooseCardsStep = (props: IChooseCardStepProps) => {
    const { labels, cardSettings } = useContext(AppSettingContext);
    const checkboxRef = useRef(null);

    //#region STATE PROPERTIES

    const [stepModel, setStepModel] = useState<IChooseCardStepModel>({
        quantity: props.stepModel?.quantity ?? 1,
        value: props.stepModel?.value === 0 ? null : props.stepModel.value
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isModelValid, setIsModelValid] = useState<boolean>();
    const [modelErrors, setModelErrors] = useState({
        quantity: '',
        value: '',
        total: ''
    });

    //#endregion

    //#region USE EFFECT HOOKS

    useEffect(() => {
        setIsModelValid(isValid());
    }, [stepModel]);

    useEffect(() => {
        setIsLoading(props.isSaving);
    }, [props.isSaving]);

    useEffect(() => {
        checkboxRef.current.checked = props.activeStep === BuyGiftCardStateEnum.CHOOSE_CARD_DETAILS;
    }, [props.activeStep]);

    //#endregion

    //#region HANDLERS

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const { name, value } = e.target;
        let error = '';

        if (!value) {
            setStepModel({
                ...stepModel,
                [name]: value
            });

            error = formatStringWithPlaceholders(labels.get("GiftCard.App.V2.ChooseCard.Error.FieldRequired"), `${name.charAt(0).toUpperCase()}${name.slice(1)}`);
        } else if (/^\d+$/.test(value) === false || value.includes('.') || value.includes('-')) {
            return;
        } else {
            let max = cardSettings.quantityMax;

            if (name == "value") {
                max = cardSettings.valueMax
            }

            const modifiedValue = Math.max(1, Math.min(max, Number(value)));

            setStepModel({
                ...stepModel,
                [name]: modifiedValue
            });
        }

        setModelErrors({
            ...modelErrors,
            [name]: error
        });
    };

    const handleSaveAndContinue = (event: React.MouseEvent<HTMLButtonElement>): void => {
        event.preventDefault();

        if (!isValid()) {
            return;
        }

        props.handleSubmit(stepModel);
        checkboxRef.current.checked = false;
    };

    //#endregion

    //#region COMMON HELPERS

    const handleExpandBoxCheck = (): void => {
        if (checkboxRef.current.checked && props.activeStep !== BuyGiftCardStateEnum.CHOOSE_CARD_DETAILS) {
            props.handleSetActiveStep(BuyGiftCardStateEnum.CHOOSE_CARD_DETAILS);
        }
    };

    const isValid = (): boolean => {
        const cardTotal = stepModel.quantity * stepModel.value;

        setModelErrors({
            ...modelErrors,
            total: cardTotal > cardSettings.totalValueMax ? "Invalid total amount" : ""
        });

        return ChooseCardStepModel.isValid(stepModel, cardSettings);
    };

    //#endregion

    return (
        <div className="choose box">
            <input
                type="checkbox"
                name="choose_cards_toggle"
                id="choose_cards_toggle"
                ref={checkboxRef}
                onChange={handleExpandBoxCheck}
                defaultChecked={true}
            />
            <div className="action-card">
                <h4 className="box-heading">
                    <i className="box-icon">
                        <img src="/static/options-card-page/options-card-icon-01.png" alt="icon 01" />
                    </i>
                    {labels.get("GiftCard.App.V2.ChooseCard.Title")}
                    <i className="box-arrow"
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                            checkboxRef.current.checked = !checkboxRef.current.checked;
                            handleExpandBoxCheck();
                        }}
                    ></i>
                </h4>

                {props.activeStep === BuyGiftCardStateEnum.CHOOSE_CARD_DETAILS && (
                    <div className="show">
                        {isLoading ? (
                            <LoadingComponent />
                        ) : (
                            <div className="slide-content">
                                <div className="flex-container choose-inputs">
                                    <div className="form-field quantity">
                                        <input
                                            type="text"
                                            name="quantity"
                                            id="quantity"
                                            placeholder={labels.get("GiftCard.App.V2.ChooseCard.Placeholder.Quantity")}
                                            className={modelErrors.quantity ? "react-input-error" : ""}
                                            value={stepModel.quantity}
                                            onInput={handleChange}
                                        />

                                        {modelErrors.quantity && (
                                            <i className="small-fs info react-input-error-message">
                                                {modelErrors.quantity}
                                            </i>
                                        )}
                                    </div>
                                    <div className="form-field currency">
                                        <div className="flex-container">
                                            <div style={{ display: 'flex', alignSelf: 'flex-start', gap: '6px', marginTop: '10px' }}>
                                                <span>
                                                    {labels.get("GiftCard.App.V2.ChooseCard.Separator")}
                                                </span>
                                                <span>
                                                    {labels.get("GiftCard.App.V2.ChooseCard.CurrencySymbol")}
                                                </span>
                                            </div>
                                            <div>
                                                <input
                                                    type="text"
                                                    name="value"
                                                    id="value"
                                                    placeholder={labels.get("GiftCard.App.V2.ChooseCard.Placeholder.Value")}
                                                    className={modelErrors.value ? "react-input-error" : ""}
                                                    value={stepModel.value ?? ""}
                                                    onInput={handleChange}
                                                />

                                                {modelErrors.value && (
                                                    <i className="small-fs info react-input-error-message">
                                                        {modelErrors.value}
                                                    </i>
                                                )}

                                                <i className={`small-fs info ${modelErrors.total ? "error" : ""}`}>
                                                    {labels.get("GiftCard.App.V2.ChooseCard.Tip1")}
                                                </i>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="form-field form-field-info">
                                        <p className="small-fs info" style={{ marginTop: "0" }}>
                                            {labels.get("GiftCard.App.V2.ChooseCard.Tip2")}
                                        </p>
                                        <p className="small-fs info" style={{ marginTop: "0" }}>
                                            {labels.get("GiftCard.App.V2.ChooseCard.Tip3")}
                                        </p>
                                    </div>
                                </div>

                                <button
                                    className="btn btn__full_width btn__primary large-fs btn__large"
                                    onClick={handleSaveAndContinue}
                                    disabled={!isModelValid}
                                >
                                    {labels.get("GiftCard.App.V2.ChooseCard.Button.Submit")}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChoooseCardsStep;